import {
  Employee,
  Machinery,
  Supplier,
  Truck,
  Material,
  StrapiResponse,
} from '../types/catalogs';

const API_BASE_URL = process.env.REACT_APP_API_URL || sessionStorage.getItem('backendUrl') || 'http://localhost:1337';

/**
 * Función genérica para obtener catálogos
 */
async function fetchCatalog<T>(endpoint: string, filters?: string): Promise<T[]> {
  try {
    let url = `${API_BASE_URL}/api/${endpoint}?pagination[pageSize]=1000`;

    // Por defecto, solo traer activos
    if (!filters) {
      url += '&filters[activo][$eq]=true';
    } else {
      url += filters;
    }

    url += '&sort=id:desc';

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Error fetching ${endpoint}`);
    }

    const data: StrapiResponse<T[]> = await response.json();
    return data.data;
  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    return [];
  }
}

/**
 * Obtener todos los empleados activos
 */
export async function fetchEmployees(): Promise<Employee[]> {
  return fetchCatalog<Employee>('employees');
}

/**
 * Obtener toda la maquinaria activa
 */
export async function fetchMachinery(): Promise<Machinery[]> {
  return fetchCatalog<Machinery>('machineries');
}

/**
 * Obtener todos los proveedores activos
 */
export async function fetchSuppliers(): Promise<Supplier[]> {
  return fetchCatalog<Supplier>('suppliers');
}

/**
 * Obtener todos los camiones activos
 */
export async function fetchTrucks(tipo?: 'FRESADO' | 'OBRA' | 'MEZCLA'): Promise<Truck[]> {
  const filters = tipo
    ? `&filters[activo][$eq]=true&filters[tipo][$eq]=${tipo}`
    : '&filters[activo][$eq]=true';
  return fetchCatalog<Truck>('trucks', filters);
}

/**
 * Obtener todos los materiales activos
 */
export async function fetchMaterials(): Promise<Material[]> {
  return fetchCatalog<Material>('materials');
}

/**
 * Buscar empleado por código
 */
export async function findEmployeeByCode(codigo: string): Promise<Employee | null> {
  try {
    const url = `${API_BASE_URL}/api/employees?filters[codigo][$eq]=${codigo}&filters[activo][$eq]=true`;
    const response = await fetch(url);
    if (!response.ok) return null;

    const data: StrapiResponse<Employee[]> = await response.json();
    return data.data.length > 0 ? data.data[0] : null;
  } catch (error) {
    console.error('Error finding employee:', error);
    return null;
  }
}

/**
 * Buscar maquinaria por código
 */
export async function findMachineryByCode(codigo: string): Promise<Machinery | null> {
  try {
    const url = `${API_BASE_URL}/api/machineries?filters[codigo][$eq]=${codigo}&filters[activo][$eq]=true`;
    const response = await fetch(url);
    if (!response.ok) return null;

    const data: StrapiResponse<Machinery[]> = await response.json();
    return data.data.length > 0 ? data.data[0] : null;
  } catch (error) {
    console.error('Error finding machinery:', error);
    return null;
  }
}

/**
 * Buscar camión por matrícula
 */
export async function findTruckByMatricula(matricula: string): Promise<Truck | null> {
  try {
    const url = `${API_BASE_URL}/api/trucks?filters[matricula][$eq]=${matricula}&filters[activo][$eq]=true`;
    const response = await fetch(url);
    if (!response.ok) return null;

    const data: StrapiResponse<Truck[]> = await response.json();
    return data.data.length > 0 ? data.data[0] : null;
  } catch (error) {
    console.error('Error finding truck:', error);
    return null;
  }
}

/**
 * Buscar material por código
 */
export async function findMaterialByCode(codigo: string): Promise<Material | null> {
  try {
    const url = `${API_BASE_URL}/api/materials?filters[codigo][$eq]=${codigo}&filters[activo][$eq]=true`;
    const response = await fetch(url);
    if (!response.ok) return null;

    const data: StrapiResponse<Material[]> = await response.json();
    return data.data.length > 0 ? data.data[0] : null;
  } catch (error) {
    console.error('Error finding material:', error);
    return null;
  }
}

/**
 * Crear nuevo empleado
 */
export async function createEmployee(data: {
  codigo: string;
  nombre: string;
  apellidos: string;
}): Promise<Employee | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/employees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          ...data,
          activo: true,
        },
      }),
    });

    if (!response.ok) return null;
    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error creating employee:', error);
    return null;
  }
}

/**
 * Crear nueva maquinaria
 */
export async function createMachinery(data: {
  codigo: string;
  tipo: string;
  descripcion: string;
  matricula?: string;
}): Promise<Machinery | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/machineries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          ...data,
          activo: true,
        },
      }),
    });

    if (!response.ok) return null;
    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error creating machinery:', error);
    return null;
  }
}

/**
 * Crear nuevo proveedor
 */
export async function createSupplier(data: {
  nombre: string;
  contacto?: string;
  telefono?: string;
  email?: string;
}): Promise<Supplier | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/suppliers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          ...data,
          activo: true,
        },
      }),
    });

    if (!response.ok) return null;
    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error creating supplier:', error);
    return null;
  }
}

/**
 * Crear nuevo camión
 */
export async function createTruck(data: {
  matricula: string;
  conductor?: string;
  tipo: string;
}): Promise<Truck | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/trucks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          ...data,
          activo: true,
        },
      }),
    });

    if (!response.ok) return null;
    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error creating truck:', error);
    return null;
  }
}

/**
 * Crear nuevo material
 */
export async function createMaterial(data: {
  codigo: string;
  descripcion: string;
}): Promise<Material | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/materials`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          ...data,
          activo: true,
        },
      }),
    });

    if (!response.ok) return null;
    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error creating material:', error);
    return null;
  }
}
